/**
 * GpuScriptApi — the `engine.gpu` capability module, v2 (asset-based user-space compute).
 *
 * v1 (inline Slang strings, positional bindings, numeric handles, manual destroy) is GONE —
 * superseded by the W-GPU epic (.refactor/COMPUTE-SHADERS-DESIGN.md). The v2 shape:
 *
 *   const kernel = await this.engine.gpu.loadKernel(this.shader, 'advect'); // @field asset guid
 *   const buf = kernel.createBuffer('particles', 100_000);   // reflection-sized, bound by name
 *   kernel.setUniform('dt', this.time.fixedDeltaTime);
 *   kernel.dispatchElements(100_000);                        // auto ceil(N / [NumThreads])
 *   const out = await buf.read();                            // scope-owned: no manual destroy
 *
 * Kernels are authored as `.slang` ASSETS (`import engine.compute` — IComputeKernel structs or
 * [Kernel] functions; the engine generates entry points and derives bind layouts from reflection).
 * Unlike v1's session-shared module, each behaviour receives its OWN GpuScriptApi over a
 * {@link GpuScope} (wired per-context in GameObjectEngineContext): resources auto-free on script
 * destroy, and all async is contained — loadKernel resolves a DEAD kernel (`ok:false`) instead of
 * rejecting (§14 async policy + the ScriptHost.safe analog for promises).
 *
 * Declared at the `system` trust tier (§9): raw buffers + arbitrary GPU work is a privileged
 * capability — absent from the sandbox `SafeEngineApi` by type.
 */

import { scriptApi, scriptFn } from "../scriptApi";
import {
  GpuScope,
  ComputeKernel,
  GpuBuffer,
  GpuTexture,
  createScopedBuffer,
  createScopedTexture,
  loadScopedKernel,
  type GpuElementType,
  type GpuTextureFormat,
} from "./GpuScope";

export type {
  GpuBacking,
  GpuKernelHandle,
  GpuKernelResourceInfo,
  GpuElementType,
  GpuTextureFormat,
} from "./GpuScope";
export { GpuScope, ComputeKernel, GpuBuffer, GpuTexture } from "./GpuScope";

@scriptApi("gpu", { tier: "system", version: 2 })
export class GpuScriptApi {
  constructor(private readonly _scope: GpuScope) {}

  /** Whether GPU compute is available in this runtime (gate your GPU path on this). */
  @scriptFn() isAvailable(): boolean {
    return !this._scope.disposed && this._scope.backing.isAvailable();
  }

  /**
   * Load a kernel from a compute-shader asset (`@field({ type: 'asset', assetType: 'computeShader' })`
   * guid). `kernel` selects one by name when the module declares several; omit it for single-kernel
   * modules. NEVER rejects — check `kernel.ok` / `kernel.error` (a dead kernel's methods are safe
   * no-ops, so straight-line code stays correct).
   */
  @scriptFn({ async: true }) loadKernel(shader: string, kernel?: string): Promise<ComputeKernel> {
    return loadScopedKernel(this._scope, shader, kernel);
  }

  /**
   * Create a standalone storage buffer of `count` elements (WGSL array stride — `float3` elements
   * pad to 16 bytes; `write`/`read` translate tight component arrays automatically). Prefer
   * `kernel.createBuffer(name, count)` — it sizes from reflection AND binds in one call.
   */
  @scriptFn() createBuffer(type: GpuElementType, count: number, data?: ArrayLike<number>): GpuBuffer {
    return createScopedBuffer(this._scope, type, count, data);
  }

  /** Create a 2D texture (sampled + storage usage) for kernel Load/Store access. */
  @scriptFn() createTexture(width: number, height: number, format?: GpuTextureFormat): GpuTexture {
    return createScopedTexture(this._scope, width, height, format);
  }
}
